import type { SnowpackState } from '../sim/scene.ts';
import type { InitialSnowParams } from '../sim/snowcover.ts';
import {
  elevationOfTemperature,
  lapseRatePer100m,
  TYPICAL_LAPSE_RATE,
  type TemperatureProfile,
} from '../sim/temperature.ts';
import { userPickedAvalancheOverlay } from '../state/avalanche.ts';
import {
  AVALANCHE_OVERLAY_MODES,
  conditions,
  overlay,
  scenarioStatus,
  showWindArrows,
  snowpack,
  verticalExaggeration,
  type OverlayMode,
} from '../state/store.ts';
import { formatElevation, formatNumber } from './format.ts';
import { NumberField } from './NumberField.tsx';
import { TemperatureFields } from './TemperatureFields.tsx';

const OVERLAY_GROUPS: { heading: string; items: { mode: OverlayMode; label: string }[] }[] = [
  {
    heading: 'Terrain & snow',
    items: [
      { mode: 'natural', label: 'Natural' },
      { mode: 'snowDepth', label: 'Snow depth' },
      { mode: 'slope', label: 'Slope' },
      { mode: 'forest', label: 'Forest' },
      { mode: 'temperature', label: 'Temperature' },
      { mode: 'precipPhase', label: 'Precip. phase' },
      { mode: 'stormSnow', label: 'Storm snow' },
    ],
  },
  {
    heading: 'Wind',
    items: [
      { mode: 'windLoading', label: 'Wind loading' },
      { mode: 'windSpeed', label: 'Wind speed' },
      { mode: 'shelter', label: 'Shelter (Sx)' },
    ],
  },
  {
    heading: 'Stability',
    items: [
      { mode: 'stability', label: 'Skier stability' },
      { mode: 'naturalStability', label: 'Natural release' },
      { mode: 'problem', label: 'Avalanche problem' },
      { mode: 'criticalDepth', label: 'Critical depth' },
      { mode: 'propagation', label: 'Propagation' },
    ],
  },
  {
    heading: 'Avalanche',
    items: [
      { mode: 'avDeposit', label: 'Deposit depth' },
      { mode: 'avNet', label: 'Net change' },
      { mode: 'avOrigin', label: 'Origin mix' },
      { mode: 'avMaxSpeed', label: 'Max speed' },
      { mode: 'avMaxPressure', label: 'Max pressure' },
      { mode: 'avMaxThickness', label: 'Max thickness' },
    ],
  },
];

/** Snowpack scenario progress/status, shown at the top of the conditions panel so it's always
 * visible while the worker recomputes in the background. */
function ScenarioStatusBar() {
  const s = scenarioStatus.value;
  if (s.kind === 'running') {
    return (
      <div class="scenario-status">
        <progress value={s.fraction} max={1} />
        <span>Computing snowpack… {Math.round(s.fraction * 100)}%</span>
      </div>
    );
  }
  if (s.kind === 'error') {
    return <div class="scenario-status error">Simulation failed: {s.message}</div>;
  }
  return s.ms !== null ? (
    <div class="scenario-status">Computed in {Math.round(s.ms)} ms</div>
  ) : null;
}

function updateTemperature(patch: Partial<TemperatureProfile>): void {
  conditions.value = {
    ...conditions.value,
    temperature: { ...conditions.value.temperature, ...patch },
  };
}

function updateSnow(patch: Partial<InitialSnowParams>): void {
  conditions.value = {
    ...conditions.value,
    initialSnow: { ...conditions.value.initialSnow, ...patch },
  };
}

interface SnowStats {
  meanDepth: number;
  maxDepth: number;
  coveredFraction: number;
  massTonnes: number;
}

function computeSnowStats(sp: SnowpackState): SnowStats {
  const data = sp.depth.data;
  let sum = 0;
  let max = 0;
  let covered = 0;
  for (const d of data) {
    if (d > max) max = d;
    sum += d;
    if (d > 0.01) covered++;
  }
  const n = data.length;
  const cellArea = sp.spec.cellSize * sp.spec.cellSize;
  return {
    meanDepth: n ? sum / n : 0,
    maxDepth: max,
    coveredFraction: n ? covered / n : 0,
    massTonnes: (sumOf(sp.swe.data) * cellArea) / 1000,
  };
}

function sumOf(data: Float32Array): number {
  let s = 0;
  for (const v of data) s += v;
  return s;
}

/** Conditions panel: overlay + view controls, temperature and snow-cover inputs, and stats over
 * the current snowpack grid. Shown once terrain is loaded. */
export function ConditionsPanel() {
  const t = conditions.value.temperature;
  const s = conditions.value.initialSnow;
  const lapseRate = lapseRatePer100m(t);
  const lapseWarning = lapseRate < TYPICAL_LAPSE_RATE.min || lapseRate > TYPICAL_LAPSE_RATE.max;
  const zeroElevation = elevationOfTemperature(t, 0);
  const twoDegElevation = elevationOfTemperature(t, 2);
  const sp = snowpack.value;
  const stats = sp ? computeSnowStats(sp) : null;

  return (
    <>
      <ScenarioStatusBar />

      <section class="panel-section">
        <h2>View</h2>
        {OVERLAY_GROUPS.map((group) => (
          <div key={group.heading}>
            <h3 class="segmented-heading">{group.heading}</h3>
            <div class="segmented">
              {group.items.map((o) => (
                <button
                  key={o.mode}
                  class={overlay.value === o.mode ? 'active' : ''}
                  onClick={() => {
                    overlay.value = o.mode;
                    if (AVALANCHE_OVERLAY_MODES.has(o.mode))
                      userPickedAvalancheOverlay.value = true;
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        ))}
        <NumberField
          label="Vertical exaggeration"
          value={verticalExaggeration.value}
          min={1}
          max={3}
          step={0.1}
          unit="×"
          decimals={1}
          onChange={(v) => (verticalExaggeration.value = v)}
        />
        <label class="checkbox-field">
          <input
            type="checkbox"
            checked={showWindArrows.value}
            onChange={(e) => (showWindArrows.value = (e.target as HTMLInputElement).checked)}
          />
          Wind arrows
        </label>
      </section>

      <section class="panel-section">
        <h2>Default temperature for new events</h2>
        <p class="hint">
          Used when a new storm or fair-weather event is added; existing events keep their own
          profile (edit those in the timeline below).
        </p>
        <TemperatureFields value={t} onChange={updateTemperature} />
        <dl class="info-list">
          <dt>Lapse rate</dt>
          <dd>{formatNumber(lapseRate, 2)} °C/100 m</dd>
          <dt>0 °C level</dt>
          <dd>{formatElevation(zeroElevation)}</dd>
          <dt>+2 °C level</dt>
          <dd>{formatElevation(twoDegElevation)}</dd>
        </dl>
        {lapseWarning ? (
          <p class="warning">
            Outside the typical range ({TYPICAL_LAPSE_RATE.min}–{TYPICAL_LAPSE_RATE.max} °C/100 m).
          </p>
        ) : null}
      </section>

      <section class="panel-section">
        <h2>Existing snow</h2>
        <NumberField
          label="Reference depth"
          value={s.referenceDepth * 100}
          min={0}
          max={500}
          step={5}
          unit="cm"
          onChange={(v) => updateSnow({ referenceDepth: v / 100 })}
        />
        <NumberField
          label="Reference elevation"
          value={s.referenceElevation}
          min={0}
          max={2500}
          step={10}
          unit="m"
          onChange={(v) => updateSnow({ referenceElevation: v })}
        />
        <NumberField
          label="Gradient"
          value={s.gradientPer100m * 100}
          min={-50}
          max={50}
          step={1}
          unit="cm/100 m"
          onChange={(v) => updateSnow({ gradientPer100m: v / 100 })}
        />
        <NumberField
          label="Snowline"
          value={s.snowlineElevation}
          min={0}
          max={2500}
          step={10}
          unit="m"
          onChange={(v) => updateSnow({ snowlineElevation: v })}
        />
        <NumberField
          label="Bulk density"
          value={s.density}
          min={50}
          max={600}
          step={10}
          unit="kg/m³"
          onChange={(v) => updateSnow({ density: v })}
        />
        <NumberField
          label="Shedding start"
          value={s.shedStartDeg}
          min={0}
          max={90}
          step={1}
          unit="°"
          onChange={(v) => updateSnow({ shedStartDeg: v })}
        />
        <NumberField
          label="Shedding end"
          value={s.shedEndDeg}
          min={0}
          max={90}
          step={1}
          unit="°"
          onChange={(v) => updateSnow({ shedEndDeg: v })}
        />
      </section>

      <section class="panel-section">
        <h2>Snowpack stats</h2>
        {stats ? (
          <dl class="info-list">
            <dt>Mean depth</dt>
            <dd>{formatNumber(stats.meanDepth * 100)} cm</dd>
            <dt>Max depth</dt>
            <dd>{formatNumber(stats.maxDepth * 100)} cm</dd>
            <dt>Snow-covered</dt>
            <dd>{formatNumber(stats.coveredFraction * 100)}%</dd>
            <dt>Total mass</dt>
            <dd>{formatNumber(stats.massTonnes)} t</dd>
          </dl>
        ) : null}
      </section>
    </>
  );
}
